import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { JumpQuota, CourseConfiguration } from '../types/drf-rules';

interface ValidationPanelProps {
  errors: string[];
  quota: JumpQuota;
  courseConfig: CourseConfiguration;
  actualCombinations?: number;
}

const ValidationPanel: React.FC<ValidationPanelProps> = ({ errors, quota, courseConfig, actualCombinations = 0 }) => {
  const isValid = errors.length === 0;
  const actualObstacleCount = courseConfig.obstacles.filter(obstacle => 
    obstacle.type !== 'start' && obstacle.type !== 'finish'
  ).length;
  const hasObstacles = actualObstacleCount > 0;
  
  const getStatusIcon = () => {
    if (!hasObstacles) {
      return <Info className="w-5 h-5 text-blue-500" />;
    }
    if (isValid) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getStatusColor = () => {
    if (!hasObstacles) return 'bg-blue-50 border-blue-200';
    if (isValid) return 'bg-green-50 border-green-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusText = () => {
    if (!hasObstacles) return 'Ingen spring placeret endnu';
    if (isValid) return 'Banen overholder alle DRF regler';
    return `${errors.length} regel${errors.length === 1 ? '' : 'er'} overtrådt`;
  };

  const getStatusTextColor = () => {
    if (!hasObstacles) return 'text-blue-800';
    if (isValid) return 'text-green-800';
    return 'text-red-800';
  };

  // Calculate current stats
  const actualObstacles = courseConfig.obstacles.filter(obstacle => 
    obstacle.type !== 'start' && obstacle.type !== 'finish'
  );
  
  const stats = {
    obstacles: actualObstacles.length,
    combinations: actualCombinations,
    maxHeightFences: actualObstacles.filter(o => o.height === quota.heightMax).length,
    maxHeightOxers: actualObstacles.filter(o => o.height === quota.heightMax && o.type === 'oxer').length,
    waterJumps: actualObstacles.filter(o => o.type === 'open_water').length
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Regel Validering</h3>
      
      {/* Status */}
      <div className={`p-4 rounded-lg border-2 mb-6 ${getStatusColor()}`}>
        <div className="flex items-center space-x-3">
          {getStatusIcon()}
          <div>
            <p className={`font-medium ${getStatusTextColor()}`}>
              {getStatusText()}
            </p>
            {!hasObstacles && (
              <p className="text-sm text-blue-600 mt-1">
                Vælg et værktøj og klik på banen for at begynde
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Current Stats */}
      {hasObstacles && (
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-3">Nuværende Status</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Spring:</span>
              <span className={`font-medium ${
                stats.obstacles >= quota.minObstacles && stats.obstacles <= quota.maxObstacles
                  ? 'text-green-600' : 'text-red-600'
              }`}>
                {stats.obstacles}/{quota.minObstacles}-{quota.maxObstacles}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Kombinationer:</span>
              <span className={`font-medium ${
                stats.combinations >= quota.minCombos && stats.combinations <= quota.maxCombos
                  ? 'text-green-600' : 'text-red-600'
              }`}>
                {stats.combinations}/{quota.minCombos}-{quota.maxCombos}
              </span>
            </div>

            {quota.mustHave.fencesAtMax > 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Max højde:</span>
                  <span className={`font-medium ${
                    stats.maxHeightFences >= quota.mustHave.fencesAtMax
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stats.maxHeightFences}/{quota.mustHave.fencesAtMax}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Max oxers:</span>
                  <span className={`font-medium ${
                    stats.maxHeightOxers >= quota.mustHave.oxersAtMax
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stats.maxHeightOxers}/{quota.mustHave.oxersAtMax}
                  </span>
                </div>
              </>
            )}

            {quota.waterMaxCm && (
              <div className="flex justify-between">
                <span className="text-gray-600">Vandgrav:</span>
                <span className="font-medium text-gray-900">
                  {stats.waterJumps}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div>
          <h4 className="font-medium text-red-900 mb-3 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Regelovertrædelser
          </h4>
          <div className="space-y-2">
            {errors.map((error, index) => (
              <div key={index} className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                • {error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rules Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">DRF 2025 Regler</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>• Spring højde: {quota.heightMin}-{quota.heightMax}cm</p>
          <p>• Antal spring: {quota.minObstacles}-{quota.maxObstacles}</p>
          <p>• Kombinationer: {quota.minCombos}-{quota.maxCombos}</p>
          {quota.mustHave.fencesAtMax > 0 && (
            <p>• Min. {quota.mustHave.fencesAtMax} spring på max højde, heraf {quota.mustHave.oxersAtMax} oxers</p>
          )}
          {quota.waterMaxCm && (
            <p>• Vandgrav bredde: {quota.waterMinCm ? `${quota.waterMinCm}-` : 'max '}{quota.waterMaxCm}cm</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationPanel;